import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { getTestSeriesByIdAPI, updateTestSeriesAPI, fetchCoursesAPI } from "../../utils/api";
import { showSuccess } from "../../utils/sweetAlert";
import { EmptyState, ErrorAlert, LoadingState } from "../../components/ui";
import TestSeriesForm, {
  EMPTY_EXTRAS,
  extrasFrom,
  extrasPayload,
  fieldErrorsFrom,
  timingPayload,
  validateExtras,
  validateTiming,
} from "./TestSeriesForm";
import "./CreateTestSeries.css";

function EditTestSeries() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    course: "",
    type: "free",
    visibility: "draft",
    section_toggle: false,
    nested_sections: false,
    sectional_timing: true,
    duration_minutes: "",
    ...EMPTY_EXTRAS,
  });
  const [totalMinutes, setTotalMinutes] = useState(0); // total_duration_minutes as loaded
  const [hasSections, setHasSections] = useState(false); // the structure is fixed once there are sections
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState("");

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setCoursesLoading(true);
        const response = await fetchCoursesAPI();
        if (response?.success && Array.isArray(response.data)) {
          setCourses(response.data);
        } else if (response) {
          setCoursesError("Couldn't load courses.");
        }
      } catch (err) {
        console.error("Error fetching courses:", err);
        setCoursesError("Couldn't load courses.");
      } finally {
        setCoursesLoading(false);
      }
    };
    fetchCourses();
  }, []);

  useEffect(() => {
    const fetchTestSeries = async () => {
      try {
        setIsLoading(true);
        setError("");
        const response = await getTestSeriesByIdAPI(id);

        if (response?.success && response.data) {
          const ts = response.data;
          setFormData({
            title: ts.title || ts.name || "",
            description: ts.description || "",
            course: ts.course || ts.courseId || "",
            type: ts.type || "free",
            visibility: ts.visibility || ts.status || "draft",
            section_toggle: Boolean(Number(ts.section_toggle)) || ts.section_toggle === true,
            nested_sections: Boolean(ts.nested_sections),
            // Older data has no flag and is section-wise
            sectional_timing: ts.sectional_timing !== false,
            duration_minutes: ts.duration_minutes ?? "",
            ...extrasFrom(ts),
          });
          setTotalMinutes(Number(ts.total_duration_minutes) || 0);
          setHasSections(Number(ts.sections_count ?? (ts.sections || []).length) > 0);
        } else if (response) {
          if (response.status === 404 || /not found/i.test(response.message || "")) setNotFound(true);
          else setError(response.message || "Failed to load this test series.");
        }
      } catch (err) {
        console.error("Error fetching test series:", err);
        setError("Network error. Please check your connection and try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestSeries();
  }, [id]);

  const validate = () => {
    const next = {};
    if (!formData.title.trim()) next.title = "Please enter a title.";
    if (!formData.course) next.course = "Please choose a course.";
    validateTiming(formData, next);
    validateExtras(formData, next);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validate() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await updateTestSeriesAPI(id, {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        course: formData.course,
        type: formData.type,
        visibility: formData.visibility,
        section_toggle: formData.section_toggle ? 1 : 0,
        nested_sections: formData.nested_sections ? 1 : 0,
        ...timingPayload(formData),
        ...extrasPayload(formData),
      });

      if (response?.success) {
        showSuccess("Your changes have been saved.", "Test series updated");
        navigate("/admin/test-series");
      } else if (response) {
        const fieldErrors = fieldErrorsFrom(response);
        if (Object.keys(fieldErrors).length) setErrors((prev) => ({ ...prev, ...fieldErrors }));
        else setError(response.message || "Failed to update the test series. Please try again.");
      }
    } catch (err) {
      console.error("Error updating test series:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="page">
        <EmptyState
          icon="bi bi-journal-x"
          title="Test series not found"
          message="It may have been deleted, or the link is incorrect."
          action={
            <Link to="/admin/test-series" className="btn-primary">
              <i className="bi bi-arrow-left"></i> Back to test series
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="ts-form-page page">
      <div className="page-header">
        <div>
          <button type="button" className="back-link" onClick={() => navigate("/admin/test-series")}>
            <i className="bi bi-arrow-left"></i> Test series
          </button>
          <h2>Edit test series</h2>
          <p>Update the details, visibility and behaviour of this test series.</p>
        </div>
        <div className="header-actions">
          <Link to={`/admin/test-series/${id}/sections`} className="btn-secondary">
            <i className="bi bi-layers"></i> Manage sections
          </Link>
        </div>
      </div>

      {isLoading ? (
        <div className="card">
          <LoadingState message="Loading test series…" />
        </div>
      ) : (
        <div className="ts-form-layout single">
          <form className="card card-pad" onSubmit={handleSubmit} noValidate>
            <ErrorAlert message={error} onDismiss={() => setError("")} />

            <TestSeriesForm
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              courses={courses}
              coursesLoading={coursesLoading}
              coursesError={coursesError}
              disabled={isSubmitting}
              showVisibility
              currentTotalMinutes={totalMinutes}
              sectionsLink={`/admin/test-series/${id}/sections`}
              structureLocked={hasSections}
            />

            <div className="form-actions">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => navigate("/admin/test-series")}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={isSubmitting}>
                {isSubmitting ? <span className="btn-spinner"></span> : <i className="bi bi-check2"></i>}
                Save changes
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default EditTestSeries;
